/**
 * new-log-route.rules.ts
 *
 * Where "start a new receiving log" goes. Three call sites ask (the side menu,
 * the landing page's entry button, and the log table's toolbar), and they were
 * each making the choice inline before this rule existed.
 *
 * There is no choice left to make: every surface opens the shared flow. It is
 * one UI that lays itself out for the device it is running on — the phone column
 * on a phone, a centered column with drag-and-drop capture and browser printing
 * on a desktop — so the entry points do not branch on platform, and nothing here
 * takes an `isMobile` argument. What the device decides is how the flow renders,
 * which is settled inside the flow (ScreenSizeService for capture and print
 * affordances, a viewport media query for layout), not by which route opens.
 *
 * The classic desktop form stays registered at its own route. Nothing navigates
 * there anymore; it is reachable only by a bookmark or an in-flight tab, and is
 * scheduled for removal once the shared flow has proven itself on desktop.
 */

/**
 * The shared receiving flow — line-item check-off, damage photos, structured
 * receipts. It holds `/receiving/new-log` because it is what "new log" means on
 * both surfaces now; the steps hang off it (`/receiving/new-log/scan`, `/items`…).
 */
export const SHARED_FLOW_ROUTE = '/receiving/new-log';

/**
 * The outgoing desktop form (freeform items textarea, no structured receipts).
 * Named `-classic` rather than keeping `/receiving/new-log`, so the plain name
 * belongs to the flow everyone now uses and this one reads as the older
 * alternative it is.
 */
export const LEGACY_DESKTOP_LOG_ROUTE = '/receiving/new-log-classic';

/**
 * True when `url` is either new-log route — used by the side menu to spot the
 * entry whose destination this rule owns, so it can send it to the flow.
 *
 * It accepts both because the menu's destination comes from app-settings.json:
 * whichever of the two is configured there (older deployments still name the
 * classic form), the item opens the shared flow.
 */
export function isNewLogMenuRoute(url: string): boolean {
  return url === SHARED_FLOW_ROUTE || url === LEGACY_DESKTOP_LOG_ROUTE;
}
